import PropTypes from 'prop-types';
import { Text, Link } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import UniversalContainer, {
  universalContainerDefaultProps,
  universalContainerPropTypes,
} from '../universal-container';
import {
  boolCheckboxSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import UniversalText from '../universal-text/universal-text';
import styles from './awards-2024-buttons.module.less';

function getLinkUrl(linkUrl, shouldTrack, title) {
  const isAnchorLink = linkUrl.charAt(0) === '#';
  if (isAnchorLink || !shouldTrack) {
    return linkUrl;
  }

  const modifiedUrl = setTrk({
    url: linkUrl,
    params: {
      [TrkTag.location]: 'AwardsNavigation',
      [TrkTag.element]: TrkElement.hyperlink,
      [TrkTag.copy]: title?.toLowerCase(),
      [TrkTag.sectionCategory]: 'AwardsNavigation--header',
    },
  });

  return modifiedUrl;
}

const setStickyHeaderBehavior = (flexCardElement, stickyHeaderElement) => {
  if (!flexCardElement || !stickyHeaderElement) return;
  const stickyHeaderComponent = stickyHeaderElement;
  const elementHeight = 120;

  if (flexCardElement.getBoundingClientRect().top < -elementHeight) {
    stickyHeaderComponent.style.position = 'fixed';
    stickyHeaderComponent.style.top = 0;
  } else {
    stickyHeaderComponent.style.top = '-50%';
  }
};

const Awards2024ButtonsBox = ({
  boxIconUrl,
  boxTitle,
  boxLinkUrl,
  shouldTrack,
}) => {
  const url = getLinkUrl(boxLinkUrl, shouldTrack, boxTitle);

  return (
    <Link href={url} className={styles.boxLink} data-testid="button-box-link">
      <div className={styles.boxItem}>
        {boxIconUrl && (
          <Image
            className={styles.boxIcon}
            src={boxIconUrl}
            alt="box-icon"
            backgroundColor="transparent"
          />
        )}
        <div className={styles.boxTitle}>
          <Text>{boxTitle}</Text>
        </div>
      </div>
    </Link>
  );
};

Awards2024ButtonsBox.propTypes = {
  children: PropTypes.node,
  boxIconUrl: PropTypes.string,
  boxTitle: PropTypes.string,
  boxLinkUrl: PropTypes.string,
  shouldTrack: PropTypes.bool,
};

Awards2024ButtonsBox.defaultProps = {
  children: null,
  boxIconUrl: '',
  boxTitle: '',
  boxLinkUrl: '',
  shouldTrack: false,
};

const Awards2024Buttons = ({
  container,
  title,
  subtitle,
  buttons,
  shouldTrack,
  disableStickyHeaderIntegration,
  maxColumnsInLayout,
  columns,
}) => {
  const awardsNavigationRef = useRef();

  useEffect(() => {
    const stickyHeader = document.getElementById('sticky-header-v2');
    if (stickyHeader) {
      stickyHeader.style.position = 'fixed';
      stickyHeader.style.display = 'flex';
      stickyHeader.style.top = '-50%';
    }
    const handleScroll = () => {
      setStickyHeaderBehavior(awardsNavigationRef.current, stickyHeader);
    };

    if (!disableStickyHeaderIntegration && stickyHeader) {
      window.addEventListener('scroll', handleScroll);
    } else if (stickyHeader) {
      stickyHeader.style.position = 'sticky';
      stickyHeader.style.top = '0';
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, [disableStickyHeaderIntegration]);

  return (
    <UniversalContainer
      {...container}
      data-testid="awards2024-buttons-container"
    >
      {title && <UniversalText {...title} />}
      {subtitle && (
        <UniversalText
          {...subtitle}
          defaultOptions={{ text: { textType: 'text' } }}
        />
      )}
      <div
        className={classNames([
          styles.boxItemsContainer,
          { [styles.boxItemContainerGrid]: maxColumnsInLayout },
        ])}
        style={{
          ...(maxColumnsInLayout && {
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
          }),
        }}
        ref={awardsNavigationRef}
      >
        {buttons?.map((box) => (
          <Awards2024ButtonsBox
            key={box.$unique_key}
            boxIconUrl={box.boxIconUrl}
            boxLinkUrl={box.boxLinkUrl}
            boxTitle={box.boxTitle}
            shouldTrack={shouldTrack}
          />
        ))}
      </div>
    </UniversalContainer>
  );
};

Awards2024Buttons.displayName = 'Awards2024Buttons';

Awards2024Buttons.propTypes = {
  children: PropTypes.node,
  container: universalContainerPropTypes,
  title: UniversalText.propTypes,
  subtitle: UniversalText.propTypes,
  buttons: PropTypes.array,
  shouldTrack: PropTypes.bool,
  disableStickyHeaderIntegration: PropTypes.bool,
  maxColumnsInLayout: PropTypes.bool,
  columns: PropTypes.number,
};

Awards2024Buttons.defaultProps = {
  children: null,
  container: universalContainerDefaultProps,
  title: 'Awards2024Buttons',
  subtitle: '',
  buttons: [],
  shouldTrack: false,
  disableStickyHeaderIntegration: false,
  maxColumnsInLayout: false,
  columns: 3,
};

Awards2024Buttons.getEditingConfig = (componentDocument) => {
  const maxColumnsInLayoutEditingConfig = componentDocument?.props
    ?.maxColumnsInLayout
    ? [
        stringSelectSpec(
          'columns',
          'Number of columns',
          [
            {
              value: '3',
              label: '3',
            },
            {
              value: '2',
              label: '2',
            },
            {
              value: '1',
              label: '1',
            },
          ],
          {
            help: 'Number of columns in layout on desktop',
          }
        ),
      ]
    : [];
  return [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      ['padding', 'margin', 'backgroundColor', 'backgroundImage', 'jumpLinkId'],
      `container`
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'title'
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'padding',
        'fontFamily',
        'textColor',
        'textSize',
        'textType',
      ],
      'subtitle',
      { text: { textType: 'text' } }
    ),
    boolCheckboxSpec(
      'shouldTrack',
      'Add BOA Awards Analytics Instrumentation?'
    ),
    boolCheckboxSpec(
      'disableStickyHeaderIntegration',
      'Disable StickyHeader Integration when Scrolling?'
    ),
    boolCheckboxSpec(
      'maxColumnsInLayout',
      'Add max number of columns in layout?'
    ),
    ...maxColumnsInLayoutEditingConfig,
    repeatable('buttons', 'Buttons', 'Button', () => [
      stringInputSpec('boxTitle', 'Button Title'),
      stringInputSpec('boxIconUrl', 'Button Icon URL'),
      stringInputSpec('boxLinkUrl', 'Button Link URL'),
    ]),
  ];
};
export default Awards2024Buttons;
